import {
  QueryClient,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import "@tanstack/react-router"
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  useLocation,
  useMatchRoute,
  useRouter,
} from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import { Hub } from "aws-amplify/utils"
import { SambaTheme } from "ipctg-inflow-types/InFlowThemes"
import { Suspense, useEffect } from "react"
import { RouterProvider } from "react-aria-components"

import api from "@/utilities/api"

import ErrorComponent from "@/globalComponents/organisms/ErrorComponent"
import Footer from "@/globalComponents/organisms/navigation/Footer"
import TopNavigationBar from "@/globalComponents/organisms/navigation/TopNavigationBar"
import RootNotFound from "@/globalComponents/organisms/RootNotFound"
import {
  DEFAULT_APPLE_ICON,
  DEFAULT_FAVICON,
  DEFAULT_TITLE,
} from "@/utilities/globals"
import { PAYMENT_PROCESSOR_BASE_KEY } from "@/utilities/hooks/queries/keys"
import { themeQueryOptions } from "@/utilities/hooks/queries/shared"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { getQrLookupIDFromRoute, useTheme } from "@/utilities/hooks/useTheme"
import { localStore } from "@/utilities/store"
import { DocumentThemeMode, ScannedArea } from "@/utilities/types/local"

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    pageName?: string
  }
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
  setScannedArea: (scannedArea: ScannedArea) => void
  setDocumentThemeMode: (mode: DocumentThemeMode) => void
}>()({
  beforeLoad: async ({ context, params, search }) => {
    const qrLookupID = getQrLookupIDFromRoute({
      params,
      search,
    })

    // Preseed styles if we have them cached
    const raw = qrLookupID ? localStore.getItem(`theme-${qrLookupID}`) : null
    if (raw && qrLookupID) {
      try {
        const theme = JSON.parse(raw)
        // Guessing this is a Samba Theme, yoloooooooo
        if ("locationInformation" in theme) {
          context.setScannedArea({ qrLookupID, theme: theme as SambaTheme })
        }
      } catch {
        console.log("no cached theme found")
      }
    }

    const session = await api.auth.fetchAuthSession()
    const isAuthenticated = !session.error && !!session.cognitoIDToken
    return { isAuthenticated }
  },
  loader: async ({ context, params, location }) => {
    const qrLookupID = getQrLookupIDFromRoute({
      params,
      search: location.search,
    })
    const theme = await context.queryClient.ensureQueryData(
      themeQueryOptions({ qrLookupID })
    )
    return { theme }
  },
  head: ({ loaderData, matches }) => {
    const theme = loaderData?.theme || null
    const faviconURL = theme?.light.logoURLs.favicon.svgURL || DEFAULT_FAVICON
    const appleTouchIconURL =
      theme?.light.logoURLs.favicon.appleTouchIconURL || DEFAULT_APPLE_ICON

    // Walk backwards through matches to find the first available pageName
    let pageName: string | undefined
    for (let i = matches.length - 1; i >= 0; i--) {
      const matchPageName = matches[i]?.staticData?.pageName
      if (matchPageName) {
        pageName = matchPageName
        break
      }
    }

    // Get location name from theme, or use default
    const locationName =
      theme?.locationInformation.locationName || DEFAULT_TITLE

    // Build title: use pageName if found, otherwise just location name
    const title = pageName ? `${locationName} | ${pageName}` : locationName

    return {
      meta: [{ title }],
      links: [
        { rel: "icon", type: "image/svg+xml", href: faviconURL },
        { rel: "apple-touch-icon", href: appleTouchIconURL },
      ],
    }
  },
  errorComponent: (opts) => (
    <main>
      <ErrorComponent {...opts} resolution="goHomeHome" />
    </main>
  ),
  notFoundComponent: (opts) => {
    return <RootNotFound {...opts} />
  },
  component: RootComponent,
})

function RootComponent() {
  const queryClient = useQueryClient()
  const router = useRouter()
  const matchRoute = useMatchRoute()
  const { setScannedArea, setDocumentThemeMode } = useTheme()
  const params = matchRoute({ to: "/qr/$qrLookupID", fuzzy: true })
  const location = useLocation()
  const qrLookupID =
    (params && params.qrLookupID) || location.search.qrLookupID || null
  const { data: theme } = useSuspenseQuery(themeQueryOptions({ qrLookupID }))

  useEffect(() => {
    if (theme && qrLookupID) {
      // White-label mode: populate context with the themed QR location data
      setScannedArea({
        qrLookupID,
        theme,
      })
    }
  }, [theme, qrLookupID, setScannedArea, setDocumentThemeMode])

  useEffect(() => {
    const hubListener = Hub.listen("auth", ({ payload }) => {
      const { event } = payload
      const paymentProcessorQueryKey = [PAYMENT_PROCESSOR_BASE_KEY]

      switch (event) {
        case "signedIn":
          router.invalidate()
          queryClient.resetQueries({
            queryKey: meQueryOptions().queryKey,
            refetchType: "all",
          })
          queryClient.resetQueries({
            queryKey: paymentProcessorQueryKey,
          })

          // We want to allow the auth form to invalidate the router and handle the navigation itself
          break
        case "signedOut":
          // Remove all user-specific queries from cache on sign-out
          router.invalidate()
          queryClient.resetQueries({
            queryKey: meQueryOptions().queryKey,
          })
          queryClient.resetQueries({
            queryKey: paymentProcessorQueryKey,
          })
          break
      }
    })

    return () => {
      hubListener()
    }
  }, [queryClient, router])

  return (
    <RouterProvider
      navigate={(to, options) => router.navigate({ to, ...options })}
      useHref={(to) => router.buildLocation({ to }).href}
    >
      <HeadContent />
      <TopNavigationBar />
      <main>
        <Outlet />
      </main>
      <Footer />
      <Suspense>
        <TanStackRouterDevtools position="bottom-left" />
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-left"
        />
      </Suspense>
    </RouterProvider>
  )
}
